import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { onAuthStateChange } from "./services/auth.service";
import { useAuthStore } from "./store/authStore";
import { ROUTES } from "./config/constants";
import { useUIStore } from "./store/uiStore";
import { usePackageStore } from "./store/packageStore";
import { useBookingSettingsStore } from "./store/bookingSettingsStore";
import { useServices, usePackages, useAddons } from "./hooks/useQueryData";
import { PackageConfig } from "./types";

import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import SkipLink from "./components/ui/SkipLink";
import Toast from "./components/ui/Toast";
import CookieConsent from "./components/CookieConsent";

import CustomerLayout from "./pages/customer/CustomerLayout";
import AdminLayout from "./pages/admin/AdminLayout";

// Eagerly load HomePage for better initial load
import HomePage from "./pages/HomePage";
import BookingPage from "./pages/BookingPage";
import ServiceAgreement from "./pages/ServiceAgreement";

// Legal pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));

// Lazy load other pages for code splitting
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const EmailConfirmationPage = lazy(() => import("./pages/auth/EmailConfirmationPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const PropertyPage = lazy(() => import("./pages/PropertyPage"));
const PropertiesMapPage = lazy(() => import("./pages/PropertiesMapPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ShowroomPage = lazy(() => import("./pages/ShowroomPage"));
const ShowroomProjectPage = lazy(() => import("./pages/ShowroomProjectPage"));
const CartPage = lazy(() => import("./pages/CartPage"));

// Customer Pages
const CustomerDashboard = lazy(() => import("./pages/customer/CustomerDashboard"));
const CustomerBookings = lazy(() => import("./pages/customer/CustomerBookings"));
const CustomerInvoices = lazy(() => import("./pages/customer/CustomerInvoices"));
const CustomerDeliverables = lazy(() => import("./pages/customer/CustomerDeliverables"));
const CustomerProfile = lazy(() => import("./pages/customer/CustomerProfile"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPendingApprovals = lazy(() => import("./pages/admin/AdminPendingApprovals"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminPackages = lazy(() => import("./pages/admin/AdminPackages"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminPortfolio = lazy(() => import("./components/admin/AdminPortfolioManager"));
const AdminDeliverables = lazy(() => import("./pages/admin/AdminDeliverables"));
const AdminShowroom = lazy(() => import("./pages/admin/AdminShowroom"));
const AdminShowroomDemo = lazy(() => import("./pages/admin/AdminShowroomDemo"));
const AdminInvoicesNew = lazy(() => import("./pages/admin/AdminInvoicesNew"));
const AdminInvoiceForm = lazy(() => import("./pages/admin/AdminInvoiceForm"));
const AdminInvoiceView = lazy(() => import("./pages/admin/AdminInvoiceView"));
const AdminEmailSettings = lazy(() => import("./pages/admin/AdminEmailSettings"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminCatalog = lazy(() => import("./pages/admin/AdminCatalog"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));

// Public Invoice View
const PublicInvoiceView = lazy(() => import("./pages/PublicInvoiceView"));

// Loading component with proper aria attributes
const PageLoader = () => (
  <div
    className="flex items-center justify-center min-h-screen"
    role="status"
    aria-label="Loading page"
  >
    <div
      className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
      aria-hidden="true"
    ></div>
    <span className="sr-only">Loading page content...</span>
  </div>
);

const App: React.FC = () => {
  const { setUser, setLoading } = useAuthStore();
  const theme = useUIStore((state) => state.theme);
  const showToast = useUIStore((state) => state.showToast);
  const toasts = useUIStore((state) => state.toasts);
  const removeToast = useUIStore((state) => state.removeToast);
  const setServices = usePackageStore((state) => state.setServices);
  const setPackages = usePackageStore((state) => state.setPackages);
  const setAddons = usePackageStore((state) => state.setAddons);
  const updateBookingPackages = useBookingSettingsStore((state) => state.updatePackages);

  // Use cached queries instead of direct API calls
  const { data: services = [] } = useServices();
  const { data: packages = [] } = usePackages();
  const { data: addons = [] } = useAddons();

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setLoading]);

  // Handle session expiration events
  useEffect(() => {
    const handleSessionExpiring = (event: CustomEvent) => {
      const minutes = event.detail?.minutes ?? 5;
      showToast(
        `Your session will expire in ${minutes} minute${
          minutes === 1 ? "" : "s"
        }. Any activity will extend it.`,
        "warning"
      );
    };

    const handleSessionExpired = () => {
      showToast("Your session has expired. Please sign in again to continue.", "info");
    };

    window.addEventListener("session-expiring", handleSessionExpiring as EventListener);
    window.addEventListener("session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("session-expiring", handleSessionExpiring as EventListener);
      window.removeEventListener("session-expired", handleSessionExpired);
    };
  }, [showToast]);

  // Sync cached query data to Zustand stores when data loads
  useEffect(() => {
    if (services.length > 0) {
      setServices(services);
    }
  }, [services, setServices]);

  useEffect(() => {
    if (packages.length > 0) {
      setPackages(packages);

      // Booking UI expects PackageConfig (a separate shape from `public.packages`).
      // Derive a reasonable default config from catalog data.
      const addonsByServiceId = new Map<string, string[]>();
      for (const addon of addons) {
        const list = addonsByServiceId.get(addon.serviceId) ?? [];
        list.push(addon.id);
        addonsByServiceId.set(addon.serviceId, list);
      }

      const bookingPackages: PackageConfig[] = packages.map((pkg) => ({
        id: pkg.id,
        name: pkg.title,
        details: pkg.description,
        price: Number((pkg as any).basePrice ?? (pkg as any).base_price ?? 0),
        serviceId: (pkg as any).serviceId ?? (pkg as any).service_id,
        includedAddons: Array.isArray((pkg as any).includedAddons)
          ? (pkg as any).includedAddons
          : [],
        optionalAddons: Array.isArray((pkg as any).optionalAddons)
          ? (pkg as any).optionalAddons
          : (addonsByServiceId.get((pkg as any).serviceId ?? (pkg as any).service_id) ?? []),
        enabled: (pkg as any).enabled !== false,
        order: (pkg as any).order ?? 0,
        popular: !!(pkg as any).popular,
        recommended: !!(pkg as any).recommended,
        badge: (pkg as any).badge,
        badgeText: (pkg as any).badgeText,
        originalPrice:
          (pkg as any).originalPrice !== null && (pkg as any).originalPrice !== undefined
            ? Number((pkg as any).originalPrice)
            : undefined,
        discount:
          (pkg as any).discount !== null && (pkg as any).discount !== undefined
            ? Number((pkg as any).discount)
            : undefined,
        validUntil: (pkg as any).validUntil ? new Date((pkg as any).validUntil) : undefined,
        seasonal: !!(pkg as any).seasonal,
        seasonalTheme: (pkg as any).seasonalTheme,
        features: Array.isArray((pkg as any).features) ? (pkg as any).features : undefined,
      }));

      updateBookingPackages(bookingPackages);
    }
  }, [packages, addons, setPackages, updateBookingPackages]);

  useEffect(() => {
    if (addons.length > 0) {
      setAddons(addons);
    }
  }, [addons, setAddons]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  return (
    <BrowserRouter>
      <SkipLink targetId="main-content" />
      <ScrollToTop />
      <Navbar />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
            <Route path="/auth/confirm" element={<EmailConfirmationPage />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
            <Route path={ROUTES.SERVICES} element={<ServicesPage />} />
            <Route path={ROUTES.PORTFOLIO} element={<PortfolioPage />} />
            <Route path={ROUTES.PROPERTIES_MAP} element={<PropertiesMapPage />} />
            <Route path={ROUTES.FAQ} element={<FAQPage />} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
            <Route path={ROUTES.COOKIE_POLICY} element={<CookiePolicy />} />
            <Route path={ROUTES.EMAIL_PREFERENCES} element={<EmailPreferences />} />
            <Route path={ROUTES.SERVICE_AGREEMENT} element={<ServiceAgreement />} />
            {/* AdminAnalytics route removed */}
            <Route path="/portfolio/:id" element={<PropertyPage />} />
            <Route path={ROUTES.SHOWROOM} element={<ShowroomPage />} />
            <Route path={ROUTES.SHOWROOM_PROJECT} element={<ShowroomProjectPage />} />
            <Route path={ROUTES.CONTACT} element={<ContactPage />} />
            <Route path={ROUTES.BOOKING} element={<BookingPage />} />
            <Route path={ROUTES.CART} element={<CartPage />} />

            {/* Public Invoice View */}
            <Route path="/invoices/:id" element={<PublicInvoiceView />} />

            {/* Customer Protected Routes */}
            <Route
              path={ROUTES.CUSTOMER_DASHBOARD}
              element={
                <ProtectedRoute>
                  <CustomerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CustomerDashboard />} />
              <Route path="bookings" element={<CustomerBookings />} />
              <Route path="invoices" element={<CustomerInvoices />} />
              <Route path="deliverables" element={<CustomerDeliverables />} />
              <Route path="profile" element={<CustomerProfile />} />
            </Route>

            {/* Admin Protected Routes */}
            <Route
              path={ROUTES.ADMIN_DASHBOARD}
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="invoices" element={<AdminInvoicesNew />} />
              <Route path="invoices/new" element={<AdminInvoiceForm />} />
              <Route path="invoices/:id" element={<AdminInvoiceView />} />
              <Route path="invoices/:id/edit" element={<AdminInvoiceForm />} />
              <Route path="pending-approvals" element={<AdminPendingApprovals />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="portfolio" element={<AdminPortfolio />} />
              <Route path="showroom" element={<AdminShowroom />} />
              <Route path="deliverables" element={<AdminDeliverables />} />
              <Route path="email-settings" element={<AdminEmailSettings />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="showroom-demo" element={<AdminShowroomDemo />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
      <div
        className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md"
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </BrowserRouter>
  );
};

export default App;
